import { useCallback, useRef, useState } from 'react';
import { useAccount } from 'wagmi';
import { Address, Hex, keccak256 } from 'viem';
import { useWriteContract } from '~/core/hooks/useWriteContract';

import { COMMIT_REVEAL_CONTRACT_ADDRESS, ZERO_ADDRESS, USDC_CONTRACT_ADDRESS } from '~/core/constants';
import { COMMIT_REVEAL_ABI } from '~/core/abis/commit.reveal.abi';
import { BlinkoGameParams, BlinkoToken } from '../api';
import { SESSION_KEYS } from '~/core/contexts/AbsSessionContext';

export interface BlinkoContractParams {
  token: Address;
  betAmount: bigint;
  gameSeedHash: Hex;
  algorithm: Hex;
  gameConfig: Hex;
  resolver: Address;
  deadline: bigint;
}

export interface BlinkoContractGame {
  gameId: Hex;
  params: BlinkoContractParams;
  serverSignature: Hex;
  salt: Hex;
}

export const useBlinkoContract = () => {
  const { address } = useAccount();
  const { writeContractAsync, writeContractWithoutWait } = useWriteContract();

  const loadingsRef = useRef({
    createGame: false,
  });

  // Track settlement loading state per game ID
  const [settlingGames, setSettlingGames] = useState<Record<string, boolean>>({});

  // Submits createGame on-chain and returns the tx hash immediately. The play
  // API is fired with this hash, so we don't block on the receipt here; the
  // returned receiptPromise lets the caller await confirmation before settling.
  const createGameOnChain = useCallback(async ({
    gameId,
    params,
    serverSignature,
    salt,
  }: BlinkoContractGame): Promise<{ txHash: Hex; receiptPromise: Promise<void> }> => {
    if (!address) {
      throw new Error('Wallet not connected');
    }

    if (loadingsRef.current.createGame) {
      throw new Error('Create game already in progress');
    }

    loadingsRef.current.createGame = true;

    try {
      const contractParams = {
        token: params.token,
        betAmount: params.betAmount,
        gameSeedHash: params.gameSeedHash,
        algorithm: params.algorithm,
        gameConfig: params.gameConfig,
        resolver: params.resolver,
        deadline: params.deadline,
      };

      const { hash, receiptPromise } = await writeContractWithoutWait({
        sessionKey: SESSION_KEYS.BLINKO,
        address: COMMIT_REVEAL_CONTRACT_ADDRESS,
        abi: COMMIT_REVEAL_ABI,
        functionName: 'createGame',
        args: [contractParams, serverSignature, salt],
        value: params.betAmount,
      });

      return { txHash: hash, receiptPromise };
    } catch (error) { 
      throw error;
    } finally {
      loadingsRef.current.createGame = false;
    }
  }, [address, writeContractWithoutWait]);

  const cashOutOnChain = useCallback(async ({
    gameId,
    payoutAmount,
    gameState,
    gameSeed,
    deadline,
    serverSignature,
  }: {
    gameId: Hex;
    payoutAmount: bigint;
    gameState: Hex;
    gameSeed: Hex;
    deadline: bigint;
    serverSignature: Hex;
  }): Promise<void> => {
    if (!address) {
      throw new Error('Wallet not connected');
    }

    if (settlingGames[gameId]) {
      return;
    }

    setSettlingGames(prev => ({ ...prev, [gameId]: true }));

    try {
      await writeContractAsync({
        sessionKey: SESSION_KEYS.BLINKO,
        address: COMMIT_REVEAL_CONTRACT_ADDRESS,
        abi: COMMIT_REVEAL_ABI,
        functionName: 'cashOut',
        args: [gameId, payoutAmount, gameState, gameSeed, deadline, serverSignature],
      });
    } catch (error) {
      throw error;
    } finally {
      setSettlingGames(prev => ({ ...prev, [gameId]: false }));
    }
  }, [address, writeContractAsync, settlingGames]);

  const markAsLostOnChain = useCallback(async ({
    gameId,
    gameState,
    gameSeed,
    deadline,
    serverSignature,
  }: {
    gameId: Hex;
    gameState: Hex;
    gameSeed: Hex;
    deadline: bigint;
    serverSignature: Hex;
  }): Promise<void> => {
    if (!address) {
      throw new Error('Wallet not connected');
    }

    if (settlingGames[gameId]) {
      return;
    }

    setSettlingGames(prev => ({ ...prev, [gameId]: true }));

    try {
      await writeContractAsync({
        sessionKey: SESSION_KEYS.BLINKO,
        address: COMMIT_REVEAL_CONTRACT_ADDRESS,
        abi: COMMIT_REVEAL_ABI,
        functionName: 'markGameAsLost',
        args: [gameId, gameState, gameSeed, deadline, serverSignature],
      });
    } catch (error) {
      throw error;
    } finally {
      setSettlingGames(prev => ({ ...prev, [gameId]: false }));
    }
  }, [address, writeContractAsync, settlingGames]);

  const convertApiParamsToChainParams = useCallback((
    apiParams: BlinkoGameParams,
    selectedToken: BlinkoToken
  ): BlinkoContractParams => {
    const tokenAddress = selectedToken === 'ETH' ? ZERO_ADDRESS : USDC_CONTRACT_ADDRESS;
    
    const chainParams = {
      token: tokenAddress,
      betAmount: BigInt(apiParams.betAmount),
      gameSeedHash: apiParams.gameSeedHash as Hex,
      algorithm: apiParams.algorithm as Hex,
      gameConfig: apiParams.gameConfig as Hex,
      resolver: apiParams.resolver as Address,
      deadline: BigInt(apiParams.deadline),
    };
    
    return chainParams;
  }, []);

  const calculateGameId = useCallback((serverSignature: Hex): Hex => {
    return keccak256(serverSignature);
  }, []);

  const getLoadingState = useCallback(() => {
    return { 
      createGame: loadingsRef.current.createGame,
      isSettling: Object.values(settlingGames).some(Boolean)
    };
  }, [settlingGames]);

  return {
    createGameOnChain,
    cashOutOnChain,
    markAsLostOnChain,
    convertApiParamsToChainParams,
    calculateGameId,
    getLoadingState,
    settlingGames, // Expose per-game loading map
  };
};
